/**
 * Save API Service
 *
 * Provides client-side interface for server-side save slots.
 * Works with both local development server and Netlify Functions.
 */

import {
  extractSceneState,
  extractTimelineState,
  extractUiState,
  extractPuppetConfig,
  applySceneState,
  applyTimelineState,
  applyUiState,
  applyPuppetConfig,
  WORKSPACE_SCHEMA_VERSION,
  type WorkspaceSchema,
} from '../utils/workspacePersistence';

/**
 * Save slot metadata
 */
export interface SaveSlotMetadata {
  slot: number;
  savedAt: string;
  sceneName: string;
  customName?: string;
  tabCount: number;
  toolCount: number;
}

/**
 * Save data structure
 */
export interface SaveData {
  metadata: SaveSlotMetadata;
  workspace: WorkspaceSchema;
}

/**
 * API response for list operation
 */
export interface ListSavesResponse {
  slots: (SaveSlotMetadata | null)[];
}

/**
 * Valid save slot numbers
 */
export type SaveSlot = 1 | 2 | 3;

/** Timeout for API requests in milliseconds */
const API_TIMEOUT = 5000;

/**
 * Get the API base URL based on environment.
 * - Dev: /api/saves (Vite proxy → Express)
 * - Docker/self-hosted: /api/saves (Express serves both static + API)
 * - Netlify: /.netlify/functions/saves (serverless)
 */
function getApiBaseUrl(): string {
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  // In production on Netlify, use Netlify Functions
  if (import.meta.env.PROD && !import.meta.env.VITE_SELF_HOSTED) {
    return '/.netlify/functions/saves';
  }
  // Dev or self-hosted (Docker): Express handles /api/saves directly
  return '/api/saves';
}

/**
 * Fetch with timeout using AbortController
 */
async function fetchWithTimeout(
  url: string,
  options: RequestInit = {},
  timeout: number = API_TIMEOUT
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return response;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error(
        'Server not responding. Is the save server running? (npm run server)'
      );
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * List all save slots with their metadata
 */
export async function listSaveSlots(): Promise<ListSavesResponse> {
  const baseUrl = getApiBaseUrl();
  const response = await fetchWithTimeout(`${baseUrl}/list`);

  if (!response.ok) {
    throw new Error(`Failed to list saves: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Load workspace from a save slot
 */
export async function loadFromSlot(slot: SaveSlot): Promise<SaveData> {
  const baseUrl = getApiBaseUrl();
  const response = await fetchWithTimeout(`${baseUrl}?slot=${slot}`);

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Save slot is empty');
    }
    throw new Error(`Failed to load save: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Save current workspace to a slot
 */
export async function saveToSlot(slot: SaveSlot): Promise<SaveSlotMetadata> {
  const workspace: WorkspaceSchema = {
    version: WORKSPACE_SCHEMA_VERSION,
    sceneStore: extractSceneState(),
    timelineStore: extractTimelineState(),
    uiStore: extractUiState(),
    puppetConfig: extractPuppetConfig(),
    savedAt: new Date().toISOString(),
  };

  const baseUrl = getApiBaseUrl();
  const response = await fetchWithTimeout(`${baseUrl}?slot=${slot}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ workspace }),
  });

  if (!response.ok) {
    throw new Error(`Failed to save: ${response.statusText}`);
  }

  const result = await response.json();
  return result.metadata;
}

/**
 * Delete a save slot
 */
export async function deleteSlot(slot: SaveSlot): Promise<void> {
  const baseUrl = getApiBaseUrl();
  const response = await fetchWithTimeout(`${baseUrl}?slot=${slot}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error(`Failed to delete save: ${response.statusText}`);
  }
}

/**
 * Rename a save slot with a custom name
 */
export async function renameSlot(
  slot: SaveSlot,
  customName: string
): Promise<SaveSlotMetadata> {
  const baseUrl = getApiBaseUrl();
  const response = await fetchWithTimeout(`${baseUrl}?slot=${slot}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ customName }),
  });

  if (!response.ok) {
    throw new Error(`Failed to rename save: ${response.statusText}`);
  }

  const result = await response.json();
  return result.metadata;
}

/**
 * Apply loaded save data to the application stores
 */
export function applyLoadedSave(saveData: SaveData): void {
  const workspace = saveData.workspace;

  applySceneState(workspace.sceneStore);
  applyTimelineState(workspace.timelineStore);
  applyUiState(workspace.uiStore);

  if (workspace.puppetConfig) {
    applyPuppetConfig(workspace.puppetConfig);
  }
}

/**
 * Format a date string for display
 */
export function formatSaveDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
